'use client';

import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import { ShoppingCart, Plus, Minus, Heart, Zap, Loader2, Check } from 'lucide-react';
import { useMiniCartUI } from '@/stores/mini-cart-ui';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useCartStore } from '@/stores/cart';
import { formatFeature } from '@/lib/distinguishing-features';
import { useWishlistStore } from '@/stores/wishlist';
import type { Product, KortPassform } from '@/types';
import { useCustomerTypeStore } from '@/stores/customer-type';
import { useBusinessAuthStore } from '@/stores/business-auth';
import { formatPrice, getDisplayPrice, getVatLabel, getBusinessPrice, convertToEUR } from '@/lib/price';
import { gtagEvent } from '@/components/GoogleAnalytics';
import { toast } from 'sonner';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { translateProductName } from '@/lib/product-translations';
import { useLocale } from '@/hooks/useLocale';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { t } from '@/i18n/t';
import { MonteringsBadges, SnabbfaktaKnapp, SnabbfaktaPanel } from '@/components/Snabbfakta';
import { useRef } from 'react';

const PLACEHOLDER_IMAGE = '/placeholder-product.svg';

interface ProductCardProps {
  product: Product;
  priority?: boolean;
  /** Attribut som skiljer korten på sidan åt — räknas ut av ProductGrid. */
  highlightFeatures?: string[];
  /** Passform ur TecDoc på kundens valda bil (ProductGrid hämtar för hela sidan). */
  passform?: KortPassform;
}

export function ProductCard({ product, priority, highlightFeatures, passform }: ProductCardProps) {
  const addItem = useCartStore((state) => state.addItem);
  /* Prenumerera på LISTAN, inte på metoden. `state.has` är en stabil
     funktionsreferens, så zustand såg aldrig någon ändring och komponenten
     ritades aldrig om — hjärtat blev därför inte rött förrän sidan laddades
     om, trots att varan låg i listan. Ägaren 2026-09-24: "hjärtat blir inte
     rött". */
  const iListan = useWishlistStore((state) => state.ids.has(product.id));
  const wishlistToggle = useWishlistStore((state) => state.toggle);
  const customerType = useCustomerTypeStore((state) => state.customerType);
  const b2bAuthenticated = useBusinessAuthStore((state) => state.isAuthenticated);
  const b2bDiscount = useBusinessAuthStore((state) => state.company?.discount_percent ?? 0);
  const pathname = usePathname();
  const { locale, currency, priceMarkup } = useLocale();
  const exchangeRate = useExchangeRate();
  const [quantity, setQuantity] = useState(1);
  const [imageError, setImageError] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  const coverImage = product.images?.find((img) => img.cover) || product.images?.[0];
  const imageUrl = imageError ? PLACEHOLDER_IMAGE : (coverImage?.external_url || PLACEHOLDER_IMAGE);
  const inWishlist = mounted && iListan;
  const isEur = currency === 'EUR';

  // Use default 'private' during SSR / first render to avoid hydration mismatch
  const safeCustomerType = mounted ? customerType : 'private';

  /** Format a SEK price for display — converts to EUR if on .me domain */
  const fp = (sekPrice: number): string => {
    if (isEur) return formatPrice(convertToEUR(sekPrice, exchangeRate, priceMarkup), 'EUR');
    return formatPrice(sekPrice);
  };

  // Köp-knappen visade ingenting när man tryckte (bara en toast i hörnet) —
  // "fick inte känslan att jag la till det" (ägaren 2026-09-16). Nu: kort
  // snurr, sedan "Tillagd" med bock, och rutan med varukorg + komplement öppnas.
  const [laggerTill, setLaggerTill] = useState<'nej' | 'pagar' | 'klar'>('nej');
  const handleAddToCart = () => {
    if (laggerTill === 'pagar') return;
    setLaggerTill('pagar');
    addItem(product, quantity);
    setTimeout(() => {
      setLaggerTill('klar');
      useMiniCartUI.getState().open(product.id);
      setTimeout(() => setLaggerTill('nej'), 1800);
    }, 350);
    gtagEvent('add_to_cart', {
      currency,
      value: isEur ? convertToEUR(product.price * quantity, exchangeRate, priceMarkup) : product.price * quantity,
      items: [{ item_id: product.reference, item_name: translateProductName(product.name), quantity, price: isEur ? convertToEUR(product.price, exchangeRate, priceMarkup) : product.price, ...(product.manufacturer_name && { item_brand: product.manufacturer_name }), ...(product.category_name && { item_category: product.category_name }) }],
    });
    setQuantity(1);
  };

  const handleToggleWishlist = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    /* Ingen inloggningsspärr. Att spara en del säger "intresserad men inte
       redo" — att svara på det med en inloggningsruta kräver det största
       steget för det minsta. Listan ligger i webbläsaren och slås ihop med
       kontot om kunden loggar in senare. Ägaren 2026-09-24. */
    try {
      const added = await wishlistToggle(product.id);
      toast.success(added
        ? t(locale, 'product.addedToWishlist')
        : t(locale, 'product.removedFromWishlist')
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t(locale, 'common.error'));
    }
  };

  /** VAT label — .me shows "Excl. VAT" always (export, no VAT) */
  const [faktaOppen, setFaktaOppen] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const vatLabel = isEur ? t(locale, 'product.exclVat') : getVatLabel(safeCustomerType);

  return (
    /* `relative` bär den utsträckta länken nedan. Kortet är 509 px högt men bara
       bilden, titeln och Köp-knappen var klickbara — artikelnummer, märke, pris och
       leveranstid låg som ett dött fält mitt i. Clarity 2026-09-08: /sok hade döda
       klick i ~30 sessioner på tre dygn, spridda över alla sorters sökningar, utan
       ett enda JS-fel. Ett kort som ser ut som en länk ska bete sig som en. */
    <Card
      className="group overflow-hidden relative isolate"
      onMouseEnter={() => {
        if (typeof window === 'undefined' || !window.matchMedia('(hover: hover)').matches) return;
        hoverTimer.current = setTimeout(() => setFaktaOppen(true), 350);
      }}
      onMouseLeave={() => { if (hoverTimer.current) clearTimeout(hoverTimer.current); setFaktaOppen(false); }}
    >
      {faktaOppen && (
        <SnabbfaktaPanel
          namn={translateProductName(product.name)}
          kf={product.key_features}
          referens={product.reference}
          marke={product.manufacturer_name}
          passar={product.vehicle_summary?.makes?.length ? `${locale === 'sv' ? 'Passar' : 'Fits'} ${product.vehicle_summary.makes.join(', ')}${product.vehicle_summary.models > 1 ? ` · ${product.vehicle_summary.models} ${locale === 'sv' ? 'modeller' : 'models'}` : ''}` : null}
          onClose={() => setFaktaOppen(false)}
          sv={locale === 'sv'}
        />
      )}
      <Link href={`/produkt/${product.slug || product.id}`}>
        <div className="aspect-square relative overflow-hidden bg-secondary">
          <Image
            src={imageUrl}
            alt={product.manufacturer_name ? `${translateProductName(product.name)} ${locale === 'sv' ? 'från' : 'by'} ${product.manufacturer_name}` : translateProductName(product.name)}
            fill
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw"
            className="object-contain p-4 group-hover:scale-105 transition-transform"
            priority={priority}
            onError={() => setImageError(true)}
            referrerPolicy="no-referrer"
          />
          {mounted && (
            <SnabbfaktaKnapp oppen={faktaOppen} onToggle={() => setFaktaOppen((o) => !o)} etikett={locale === 'sv' ? 'Snabbfakta' : 'Quick facts'} />
          )}
          {/* Wishlist heart */}
          {mounted && (
            <button
              onClick={handleToggleWishlist}
              className="absolute top-1.5 left-1.5 z-10 h-9 w-9 rounded-full bg-background/80 backdrop-blur-sm flex items-center justify-center hover:bg-background transition-colors shadow-sm"
              aria-label={inWishlist ? t(locale, 'product.removeFromWishlist') : t(locale, 'product.addToWishlist')}
            >
              <Heart
                className={`h-4 w-4 transition-colors ${
                  inWishlist ? 'fill-red-500 text-red-500' : 'text-muted-foreground hover:text-red-400'
                }`}
              />
            </button>
          )}
        </div>
      </Link>

      <CardContent className="p-2.5 sm:p-4">
        <Link
          href={`/produkt/${product.slug || product.id}`}
          className="after:absolute after:inset-0 after:content-['']"
        >
          <h3 className="font-heading font-semibold text-xs sm:text-sm line-clamp-2 hover:text-primary transition-colors">
            {translateProductName(product.name)}
          </h3>
        </Link>
        <MonteringsBadges namn={product.name} kf={product.key_features} />
        {passform && (
          <p title={locale === 'sv' ? passform.text : undefined}
            className={`mt-1 inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[10px] sm:text-xs font-semibold ${
              passform.status === 'passar' ? 'bg-green-50 text-green-800 border border-green-200'
              : passform.status === 'villkor' ? 'bg-amber-50 text-amber-900 border border-amber-200'
              : 'bg-red-50 text-red-800 border border-red-200'}`}>
            <span className="truncate">
              {passform.status === 'passar' ? t(locale, 'product.fitExact')
                : locale === 'sv' ? passform.text
                : t(locale, passform.status === 'villkor' ? 'product.fitCondition' : 'product.fitNo')}
            </span>
          </p>
        )}
        <p className="text-[11px] sm:text-sm text-muted-foreground mt-1 truncate">
          {product.reference}
        </p>
        {product.manufacturer_name && (
          <p className="text-[11px] sm:text-sm text-muted-foreground truncate">
            {product.manufacturer_name}
          </p>
        )}
        {/* Vilka bilar passar delen? Katalogen har 4 393 produkter som alla heter
            "Bromsbeläggssats, Framaxel"; utan vald bil är märket det enda som
            skiljer dem åt i en lista. Visas bara när namnet inte redan säger
            det, och bara när ingen bil är vald (API:et utelämnar fältet då). */}
        {(() => {
          const vs = product.vehicle_summary;
          if (!vs?.makes?.length) return null;
          const namnUpper = (product.name || '').toUpperCase();
          const okanda = vs.makes.filter((m) => !namnUpper.includes(m.toUpperCase()));
          if (okanda.length === 0) return null;
          // TecDoc lagrar märken i VERSALER ("CITROËN", "ALFA ROMEO"). Versaler i
          // en produktlista läses som skrik; däremot är "BMW" och "DS" äkta versaler.
          const marke = (m: string) =>
            m.split(/([ -])/).map((d) => (d.length <= 3 ? d : d[0] + d.slice(1).toLowerCase())).join('');
          const txt = `Passar ${vs.makes.map(marke).join(', ')}${vs.models > 1 ? ` · ${vs.models} modeller` : ''}`;
          return (
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate" title={txt}>
              {txt}
            </p>
          );
        })()}

        {product.key_features && Object.keys(product.key_features).length > 0 && (() => {
          const kf = product.key_features;

          // Skiljer något attribut den här produkten från de andra på sidan?
          // Då är det viktigare än den kurerade listan nedan. "Generator-laddström
          // [A] = 140" blir "Laddström 140 A"; enheten står i hakparentesen.
          if (highlightFeatures?.length) {
            const utmarkande = highlightFeatures
              .map((namn) => { const v = kf[namn]; return v ? formatFeature(namn, v) : null; })
              .filter((x): x is string => x !== null);
            if (utmarkande.length) {
              return (
                <p className="text-[10px] sm:text-xs font-medium text-foreground/80 mt-0.5 truncate" title={utmarkande.join(' · ')}>
                  {utmarkande.join(' · ')}
                </p>
              );
            }
          }

          const specs: string[] = [];
          const pos = kf['Position'] || kf['Delens position'];
          if (pos) specs.push(pos);
          if (kf['Ytterdiameter [mm]']) specs.push(`Ø${kf['Ytterdiameter [mm]']}mm`);
          if (kf['Innerdiameter [mm]']) specs.push(`Ø${kf['Innerdiameter [mm]']}mm inre`);
          if (kf['Diameter (mm)']) specs.push(`Ø${kf['Diameter (mm)']}mm`);
          const len = kf['Längd[mm]'] || (kf['Längd (cm)'] ? `${parseFloat(kf['Längd (cm)']) * 10}` : null);
          if (len) specs.push(`${len}mm`);
          if (kf['Tjocklek [mm.]']) specs.push(`${kf['Tjocklek [mm.]']}mm tjock`);
          if (kf['Spänning [V]']) specs.push(`${kf['Spänning [V]']}V`);
          if (kf['Bromsskivetyp']) specs.push(kf['Bromsskivetyp']);
          return specs.length > 0 ? (
            <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 truncate">
              {specs.slice(0, 3).join(' · ')}
            </p>
          ) : null;
        })()}
        {mounted && pathname.startsWith('/foretag') && b2bAuthenticated() && b2bDiscount > 0 ? (
          <>
            <p className="text-[10px] sm:text-xs text-muted-foreground line-through mt-1.5 sm:mt-2">
              {fp(product.price / 1.25)}
            </p>
            <p className="text-base sm:text-xl font-bold text-green-700">
              {fp(getBusinessPrice(product.price, b2bDiscount))}
            </p>
            <p className="text-[10px] sm:text-xs text-green-700 font-medium">
              {locale === 'sv' ? 'Ditt pris' : 'Your price'} &middot; {t(locale, 'product.exclVat')}
            </p>
          </>
        ) : product.campaign_price != null ? (
          <>
            {product.campaign_discount_type === 'percent' && product.campaign_discount_value && (
              <span className="inline-block bg-red-600 text-white text-[10px] sm:text-xs font-bold px-1.5 py-0.5 rounded mt-1.5 sm:mt-2">
                -{product.campaign_discount_value}%
              </span>
            )}
            <p className="text-[10px] sm:text-xs text-muted-foreground line-through mt-0.5">
              {isEur ? fp(product.price) : formatPrice(getDisplayPrice(product.price, safeCustomerType))}
            </p>
            <p className="text-base sm:text-xl font-bold text-red-600">
              {isEur ? fp(product.campaign_price) : formatPrice(getDisplayPrice(product.campaign_price, safeCustomerType))}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              {vatLabel}
            </p>
          </>
        ) : (
          <>
            <p className="text-base sm:text-xl font-bold mt-1.5 sm:mt-2">
              {isEur ? fp(product.price) : formatPrice(getDisplayPrice(product.price, safeCustomerType))}
            </p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">
              {vatLabel}
            </p>
          </>
        )}
        {/* Delivery indicator */}
        <div className="mt-1">
          {product.express ? (
            <span className="inline-flex items-center gap-0.5 text-[10px] sm:text-xs font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
              <Zap className="h-3 w-3" /> {t(locale, 'product.deliveryExpress')}
            </span>
          ) : (
            <span className="text-[10px] sm:text-xs font-medium text-green-700">{t(locale, 'product.delivery')}</span>
          )}
        </div>
      </CardContent>

      {/* z-10: annars ligger den utsträckta länken över antalsväljaren och Köp. */}
      <CardFooter className="relative z-10 p-2.5 sm:p-4 pt-0">
        {/* flex-wrap, inte bara sm:flex-row. Radläget slog till på VIEWPORT-bredd
            (sm = 640 px) oavsett hur smalt själva KORTET var. På söksidan, där
            filterkolumnen äter plats och rutnätet ändå går i fyra spalter från
            1024 px, blev kortet 170 px brett medan antalsväljaren (98 px) och
            köpknappen (82 px) tillsammans kräver 180. Knappen stack därför ut
            52 px utanför kortet i hela spannet 1024-1279 px — uppmätt 2026-09-08.
            Katalogsidans kort staplar dem och drabbades aldrig.
            Med wrap faller knappen ner på egen rad när den inte får plats,
            oberoende av vad viewporten säger. */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-1.5 sm:gap-2 w-full">
          <div className="flex items-center border rounded-md self-center sm:self-start">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 sm:h-8 sm:w-8"
              onClick={() => setQuantity(Math.max(1, quantity - 1))}
              disabled={product.quantity <= 0 && !product.allow_backorder}
              aria-label={t(locale, 'product.decreaseQty')}
            >
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="w-7 sm:w-8 text-center text-xs sm:text-sm">{quantity}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 sm:h-8 sm:w-8"
              onClick={() => setQuantity(quantity + 1)}
              disabled={product.quantity <= 0 && !product.allow_backorder}
              aria-label={t(locale, 'product.increaseQty')}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
          <Button
            className="flex-1 min-w-[5.5rem] h-8 sm:h-9 text-xs sm:text-sm"
            onClick={handleAddToCart}
            disabled={product.quantity <= 0 && !product.allow_backorder}
          >
            {laggerTill === 'pagar' ? (
              <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" aria-label={t(locale, 'product.buy')} />
            ) : laggerTill === 'klar' ? (
              <><Check className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />{t(locale, 'product.added')}</>
            ) : (
              <><ShoppingCart className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1 sm:mr-2" />{t(locale, 'product.buy')}</>
            )}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
